import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { type ReactNode, useEffect } from "react";

import loginArt from "../assets/loginArt.webp";

interface AuthPageLayoutProps {
  /** A line under the brand that names the page, such as "Reset your password". */
  title?: ReactNode;
  children: ReactNode;
}

/**
 * The signed-out page frame shared by /login, /forgot-password and /reset-password: the
 * form card, and the login art beside it from `lg` or behind it below.
 */
export function AuthPageLayout({ title, children }: AuthPageLayoutProps) {
  // globals.css reserves the scrollbar track; on this fixed-height page it shows as a strip beside the art.
  useEffect(() => {
    const rootStyle = document.documentElement.style;
    rootStyle.scrollbarGutter = "auto";
    return () => {
      rootStyle.scrollbarGutter = "";
    };
  }, []);

  // The crop anchors right of center, where the gate is, with a separate anchor for phones.
  return (
    <div className="relative isolate flex min-h-svh bg-background">
      <div className="flex w-full items-center justify-center p-4 lg:w-[30rem] lg:shrink-0 lg:p-10">
        <Card className="w-full max-w-sm bg-card/80 supports-backdrop-filter:backdrop-blur-md lg:bg-transparent lg:shadow-none lg:ring-0 lg:backdrop-blur-none">
          <CardHeader>
            <CardTitle className="text-lg font-bold tracking-tight lg:text-2xl">
              Loot <span className="text-primary">OS</span>
            </CardTitle>
            {title ? <CardDescription>{title}</CardDescription> : null}
          </CardHeader>
          <CardContent className="grid gap-4">{children}</CardContent>
        </Card>
      </div>
      <div className="absolute inset-0 -z-10 overflow-hidden lg:relative lg:inset-auto lg:z-0 lg:flex-1">
        <img
          src={loginArt}
          alt=""
          aria-hidden
          decoding="async"
          fetchPriority="high"
          draggable={false}
          className="absolute inset-0 size-full select-none object-cover object-[85%_45%] sm:object-[92%_45%]"
        />
        <div className="absolute inset-0 bg-background/50 lg:hidden" />
        <div className="absolute inset-y-0 left-0 hidden w-48 bg-linear-to-r from-background to-transparent lg:block" />
      </div>
    </div>
  );
}
